import { useState, useEffect } from 'react'
import {
  Clock,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Users,
  Boxes,
  ClipboardList,
  CheckSquare,
  MessageSquare,
  FileText,
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DropdownResults } from '@/components/ui/dropdown-results'
import { MobileSlideInMenu } from '@/components/layout/MobileSlideInMenu'
import { useHistory } from '@/hooks/useHistory'
import { useDropdownNavigation } from '@/hooks/useDropdownNavigation'
import { cn } from '@/lib/utils/cn'
import { SearchResult } from '@/types/search'
import { LucideIcon } from 'lucide-react'

interface HistoryStackProps {
  className?: string
}

export function HistoryStack({ className }: HistoryStackProps) {
  const { history, clearHistory } = useHistory()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = location.pathname + location.search
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [canNavigateBack, setCanNavigateBack] = useState(false)
  const [canNavigateForward, setCanNavigateForward] = useState(false)
  const [isAtEndOfHistory, setIsAtEndOfHistory] = useState(true)

  // Close dropdown/menu when user navigates to a new page by forcing re-mount
  const locationKey = `${location.pathname}${location.search}`

  // Simple and reliable navigation state tracking
  useEffect(() => {
    const updateNavigationState = () => {
      const historyLength = window.history.length
      setCanNavigateBack(historyLength > 1)

      // Forward navigation is only possible if we have history and haven't navigated to a new page
      // The key insight: forward is disabled by default and only enabled when user uses back button
      setCanNavigateForward(historyLength > 1 && !isAtEndOfHistory)
    }

    updateNavigationState()
  }, [locationKey, isAtEndOfHistory])

  // Track navigation position - start at end of history
  useEffect(() => {
    setIsAtEndOfHistory(true)

    // When user navigates via back/forward buttons, we're no longer at the end
    const handlePopState = () => {
      setIsAtEndOfHistory(false)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Track when user navigates to a new page (resets forward navigation)
  useEffect(() => {
    // Use a ref to track the previous location to avoid stale closure issues
    const previousLocationRef = { current: location }

    const checkForNewPage = () => {
      const currentLocation = location
      const previousLocation = previousLocationRef.current

      // If the pathname changed (not just search params), it's a new page
      if (currentLocation.pathname !== previousLocation.pathname) {
        // User navigated to a new page - disable forward navigation
        setIsAtEndOfHistory(true)
      }

      previousLocationRef.current = currentLocation
    }

    // Check after navigation completes
    const timeoutId = setTimeout(checkForNewPage, 10)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [location])

  const handleGoBack = () => {
    window.history.back()
  }

  const handleGoForward = () => {
    window.history.forward()
  }

  // Ensure dropdown/menu is closed on mount/navigation
  useEffect(() => {
    setIsDropdownOpen(false)
    setIsMobileMenuOpen(false)
  }, [locationKey])

  // Map icon components back to their corresponding node types
  const getTypeFromIcon = (icon?: LucideIcon): SearchResult['type'] => {
    if (!icon) return 'message' // fallback

    if (icon === Users) return 'user'
    if (icon === Boxes) return 'workspace'
    if (icon === ClipboardList) return 'project'
    if (icon === CheckSquare) return 'task'
    if (icon === MessageSquare) return 'message'
    if (icon === FileText) return 'document'

    return 'message' // fallback for unknown icons
  }

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`

    return date.toLocaleDateString()
  }

  // Transform history items to SearchResult format (reverse chronological order - most recent first)
  const historyResults = history
    .slice()
    .reverse()
    .map((item) => ({
      id: item.id,
      type: getTypeFromIcon(item.icon), // Map the icon back to the correct node type
      title: item.title,
      subtitle: formatTimestamp(item.timestamp),
      url: item.path,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      node: {} as any, // We'll use any for history items since they don't represent actual nodes
    })) as SearchResult[]

  // Use the consolidated navigation hook
  const {
    selectedIndex,
    resultItemRefs,
    viewAllButtonRef,
    handleKeyDown,
    handleResultClickForDropdown,
    handleViewAllClick,
  } = useDropdownNavigation({
    isOpen: isDropdownOpen,
    results: historyResults,
    hasViewAllButton: true,
    shouldShowViewAllButton: true,
    onDropdownOpenChange: setIsDropdownOpen,
    onResultClick: (result: SearchResult) => {
      if (result.url && result.url !== currentPath) {
        navigate(result.url)
      }
    },
    onViewAllClick: () => navigate('/history'),
  })

  // Navigation handlers are defined above with proper state tracking

  const backButtonTitle = 'Go back'
  const forwardButtonTitle = 'Go forward'

  if (history.length === 0) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative', className)}
        title="No recents"
        disabled
      >
        <Clock className="h-5 w-5" />
      </Button>
    )
  }

  const handleHistoryItemClick = (result: SearchResult) => {
    if (result.url && result.url !== currentPath) {
      navigate(result.url)
    }
    setIsMobileMenuOpen(false)
  }

  const handleMobileViewAllClick = () => {
    navigate('/history')
    setIsMobileMenuOpen(false)
  }

  if (history.length === 0) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative', className)}
        title="No recents"
        disabled
      >
        <Clock className="h-5 w-5" />
      </Button>
    )
  }

  return (
    <div key={locationKey} className={cn('flex items-center gap-1', className)}>
      {/* Desktop Version */}
      <div className="hidden lg:flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleGoBack}
          disabled={!canNavigateBack}
          title={backButtonTitle}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleGoForward}
          disabled={!canNavigateForward}
          title={forwardButtonTitle}
        >
          <ArrowRight className="h-5 w-5" />
        </Button>
        <DropdownMenu onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              title={`Recent pages (${history.length})`}
            >
              <Clock className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            className="w-80 max-h-96 overflow-y-auto p-0"
            onKeyDown={handleKeyDown}
          >
            <DropdownResults
              results={historyResults}
              query="recents"
              selectedIndex={selectedIndex}
              onResultClick={handleResultClickForDropdown}
              label="Recent Activity"
              labelAction={
                history.length > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearHistory}
                    className="h-auto p-1 text-muted-foreground hover:text-destructive"
                    title="Clear history"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                ) : undefined
              }
              showViewAllButton={true}
              onViewAllClick={handleViewAllClick}
              resultItemRefs={resultItemRefs}
              viewAllButtonRef={viewAllButtonRef}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile Version */}
      <div className="flex lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleGoBack}
          disabled={!canNavigateBack}
          title={backButtonTitle}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleGoForward}
          disabled={!canNavigateForward}
          title={forwardButtonTitle}
        >
          <ArrowRight className="h-5 w-5" />
        </Button>

        <MobileSlideInMenu
          id="mobile-history"
          heading="Recent Activity"
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          mode="top"
        >
          <div className="space-y-2">
            {historyResults.length > 0 ? (
              <>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {historyResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleHistoryItemClick(result)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors rounded-xl',
                        'hover:bg-muted active:bg-muted',
                        result.url === currentPath && 'bg-primary/10 text-primary'
                      )}
                    >
                      <div className="flex-shrink-0">
                        {result.type === 'user' && (
                          <Users className="h-5 w-5 text-muted-foreground" />
                        )}
                        {result.type === 'workspace' && (
                          <Boxes className="h-5 w-5 text-muted-foreground" />
                        )}
                        {result.type === 'project' && (
                          <ClipboardList className="h-5 w-5 text-muted-foreground" />
                        )}
                        {result.type === 'task' && (
                          <CheckSquare className="h-5 w-5 text-muted-foreground" />
                        )}
                        {result.type === 'message' && (
                          <MessageSquare className="h-5 w-5 text-muted-foreground" />
                        )}
                        {result.type === 'document' && (
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{result.title}</div>
                        <div className="text-xs text-muted-foreground">{result.subtitle}</div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="px-2 pt-2 border-t">
                  <Button variant="outline" className="w-full" onClick={handleMobileViewAllClick}>
                    View All History
                  </Button>
                </div>
              </>
            ) : (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </MobileSlideInMenu>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="relative"
          title={`Recent pages (${history.length})`}
        >
          <Clock className="h-5 w-5" />
        </Button>
      </div>
    </div>
  )
}
